import React, { useState, useEffect } from 'react'
import { 
  Database, Download, Upload, Save, 
  AlertTriangle, CheckCircle, Clock, 
  HardDrive, RefreshCw, Shield, Archive
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

interface Backup {
  id: string
  filename: string
  size: number
  created_at: string
  type: 'manual' | 'scheduled'
  status: 'completed' | 'failed' | 'in_progress'
  description?: string
  tables_included?: string[]
}

interface BackupConfig {
  auto_backup_enabled: boolean
  backup_schedule: 'daily' | 'weekly' | 'monthly'
  backup_time: string
  retention_days: number
  include_uploads: boolean
  compress_backup: boolean
  encrypt_backup: boolean
}

export const BackupRestore: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([])
  const [config, setConfig] = useState<BackupConfig>({
    auto_backup_enabled: true,
    backup_schedule: 'daily',
    backup_time: '02:00',
    retention_days: 30,
    include_uploads: true,
    compress_backup: true,
    encrypt_backup: false
  })
  
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [systemStatus, setSystemStatus] = useState({
    database_size: 0,
    uploads_size: 0,
    total_backups: 0,
    last_backup: null as string | null
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [backupsData, configData, statusData] = await Promise.all([
        apiClient.get('/api/backups'),
        apiClient.get('/api/backups/config'),
        apiClient.get('/api/system/status')
      ])
      setBackups(backupsData)
      setConfig(configData)
      setSystemStatus(statusData)
    } catch (error) {
      console.error('Failed to load backup data:', error)
      toast.error('Failed to load backup data')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateBackup = async () => {
    setCreating(true)
    try {
      const response = await apiClient.post('/api/backups/create', {
        description: `Manual backup - ${format(new Date(), 'MMM d, yyyy h:mm a')}`,
        include_uploads: config.include_uploads,
        compress: config.compress_backup,
        encrypt: config.encrypt_backup
      })
      
      toast.success('Backup created successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create backup')
    } finally {
      setCreating(false)
    }
  }

  const handleRestoreBackup = async () => {
    if (!selectedBackup) {
      toast.error('Please select a backup to restore')
      return
    }

    if (!confirm('WARNING: This will replace all current data with the backup. This action cannot be undone. Continue?')) {
      return
    }

    setRestoring(true)
    try {
      await apiClient.post(`/api/backups/${selectedBackup}/restore`)
      toast.success('Backup restored successfully. Please log in again.')
      
      // Force logout and redirect after restore
      setTimeout(() => {
        window.location.href = '/login'
      }, 2000)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup')
    } finally {
      setRestoring(false)
    }
  }

  const handleDownloadBackup = async (backupId: string) => {
    try {
      const response = await apiClient.get(`/api/backups/${backupId}/download`, {
        responseType: 'blob'
      })
      
      const backup = backups.find(b => b.id === backupId)
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', backup?.filename || 'backup.sql')
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Backup downloaded successfully')
    } catch (error: any) {
      toast.error('Failed to download backup')
    }
  }

  const handleDeleteBackup = async (backupId: string) => {
    if (!confirm('Are you sure you want to delete this backup?')) return
    
    try {
      await apiClient.delete(`/api/backups/${backupId}`)
      toast.success('Backup deleted successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete backup')
    }
  }

  const handleUploadBackup = async () => {
    if (!uploadFile) {
      toast.error('Please select a backup file to upload')
      return
    }

    const formData = new FormData()
    formData.append('file', uploadFile)

    try {
      await apiClient.post('/api/backups/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      toast.success('Backup uploaded successfully')
      setUploadFile(null)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to upload backup')
    }
  }

  const handleSaveConfig = async () => {
    try {
      await apiClient.put('/api/backups/config', config)
      toast.success('Backup configuration saved')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration')
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Backup & Restore</h2>
        <p className="text-gray-600">Manage system backups and data recovery</p>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Database Size</p>
              <p className="text-xl font-bold">{formatFileSize(systemStatus.database_size)}</p>
            </div>
            <Database className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Uploads Size</p>
              <p className="text-xl font-bold">{formatFileSize(systemStatus.uploads_size)}</p>
            </div>
            <HardDrive className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Backups</p>
              <p className="text-xl font-bold">{systemStatus.total_backups}</p>
            </div>
            <Archive className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Last Backup</p>
              <p className="text-sm font-semibold">
                {systemStatus.last_backup 
                  ? format(parseISO(systemStatus.last_backup), 'MMM d')
                  : 'Never'
                }
              </p>
            </div>
            <Clock className="w-8 h-8 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleCreateBackup}
            disabled={creating}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center disabled:bg-gray-400"
          >
            <Save className="w-4 h-4 mr-2" />
            {creating ? 'Creating...' : 'Create Backup Now'}
          </button>
          
          <button
            onClick={handleRestoreBackup}
            disabled={!selectedBackup || restoring}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center disabled:bg-gray-400"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {restoring ? 'Restoring...' : 'Restore Selected'}
          </button>
          
          <div className="flex items-center">
            <input
              type="file"
              accept=".sql,.zip,.tar.gz"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="hidden"
              id="backup-upload"
            />
            <label
              htmlFor="backup-upload"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center cursor-pointer"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Backup
            </label>
            {uploadFile && (
              <button
                onClick={handleUploadBackup}
                className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Upload {uploadFile.name}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Backup Configuration */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Backup Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={config.auto_backup_enabled}
                onChange={(e) => setConfig({ ...config, auto_backup_enabled: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Enable automatic backups</span>
            </label>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Backup Schedule</label>
                <select
                  value={config.backup_schedule}
                  onChange={(e) => setConfig({ ...config, backup_schedule: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                  disabled={!config.auto_backup_enabled}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Backup Time</label>
                <input
                  type="time"
                  value={config.backup_time}
                  onChange={(e) => setConfig({ ...config, backup_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  disabled={!config.auto_backup_enabled}
                />
              </div>
            </div>
          </div>
          
          <div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Retention Period (days)</label>
              <input
                type="number"
                min="7"
                value={config.retention_days}
                onChange={(e) => setConfig({ ...config, retention_days: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.include_uploads}
                  onChange={(e) => setConfig({ ...config, include_uploads: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">Include uploaded files</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.compress_backup}
                  onChange={(e) => setConfig({ ...config, compress_backup: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">Compress backups</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.encrypt_backup}
                  onChange={(e) => setConfig({ ...config, encrypt_backup: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">Encrypt backups</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveConfig}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Save Configuration
          </button>
        </div>
      </div>

      {/* Backups List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Available Backups</h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {backups.map(backup => (
            <div 
              key={backup.id} 
              className={`p-4 hover:bg-gray-50 ${selectedBackup === backup.id ? 'bg-blue-50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="backup"
                    checked={selectedBackup === backup.id}
                    onChange={() => setSelectedBackup(backup.id)}
                    className="mr-3"
                  />
                  <div>
                    <div className="flex items-center">
                      <h4 className="font-medium text-gray-900">{backup.filename}</h4>
                      {backup.status === 'completed' && (
                        <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                      )}
                      {backup.status === 'failed' && (
                        <AlertTriangle className="w-4 h-4 text-red-500 ml-2" />
                      )}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                      <span>{format(parseISO(backup.created_at), 'MMM d, yyyy h:mm a')}</span>
                      <span>{formatFileSize(backup.size)}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        backup.type === 'scheduled' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {backup.type}
                      </span>
                    </div>
                    {backup.description && (
                      <p className="text-sm text-gray-600 mt-1">{backup.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownloadBackup(backup.id)}
                    className="p-2 text-blue-600 hover:text-blue-900"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteBackup(backup.id)}
                    className="p-2 text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {backups.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No backups available</p>
            <p className="text-sm mt-1">Create your first backup to get started</p>
          </div>
        )}
      </div>

      {/* Warning */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-semibold mb-1">Important Notes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Always test backups in a staging environment before restoring to production</li>
              <li>Restoring a backup will replace ALL current data</li>
              <li>Ensure all users are logged out before performing a restore</li>
              <li>Keep multiple backups in case the most recent one is corrupted</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// Add missing import
import { Trash2 } from 'lucide-react'